import { ErrorHandler, inject, Injectable } from '@angular/core';
import {
    ApplicationInsights,
    IConfig,
    IConfiguration,
    ICustomProperties,
    IExceptionTelemetry,
    ITelemetryItem,
    ITraceTelemetry,
} from '@microsoft/applicationinsights-web';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { Router } from '@angular/router';
import { ClickAnalyticsPlugin } from '@microsoft/applicationinsights-clickanalytics-js';
import { AppInsightsAngularPluginConfig, AppInsightsClickPluginConfig, redactString } from './pace-insights.conf';
import * as uuid from 'uuid';
import { truncateAppInsightsProperties } from './pace-insights.utils';

@Injectable({
    providedIn: 'root',
})
export class PaceInsightsService {
    private readonly router = inject(Router);
    private appInsights?: ApplicationInsights;
    private insightsEnabled = false;
    private clientTraceId = 'unset';
    private readonly angularPluginInstance = inject(AngularPlugin);
    private appVersion = 'unknown';
    private host: string | undefined;

    public init(connectionString: string): void {
        this.insightsEnabled = !!connectionString;
        if (this.insightsEnabled) {
            this.clientTraceId = uuid.v4();
            this.host = document.location.host;

            const clickPluginInstance = new ClickAnalyticsPlugin();

            const angularPluginConfig: IConfiguration & IConfig = {
                ...AppInsightsAngularPluginConfig,

                connectionString,

                extensions: [this.angularPluginInstance, clickPluginInstance],
                extensionConfig: {
                    [this.angularPluginInstance.identifier]: {
                        router: this.router,
                        errorServices: [new ErrorHandler()],
                    },
                    [clickPluginInstance.identifier]: AppInsightsClickPluginConfig,
                },
            };

            this.appInsights = new ApplicationInsights({
                config: angularPluginConfig,
            });

            let exposedSession = false;
            this.appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
                if (!exposedSession) {
                    console.log(this.getClientSession());
                    exposedSession = true;
                }

                envelope.data = envelope.data || {};
                envelope.data = {
                    ...envelope.data,
                    appVersion: this.appVersion,
                    clientTraceId: this.clientTraceId,
                    paceWebHost: this.host,
                };

                envelope.data = this.redactSensitiveInformation(envelope.data);
                envelope.ext = this.redactSensitiveInformation(envelope.ext);
                envelope.baseData = this.redactSensitiveInformation(envelope.baseData);

                envelope.tags = envelope.tags || {};
                envelope.tags['ai.cloud.role'] = 'pace-web';
                // Explicitly set the Azure operation ID to match our client trace ID
                // so the error modal and backend logs all share the exact same ID.
                // W3C trace ID must be a 32-character hex string, so dashes are removed for Azure compatibility.
                envelope.tags['ai.operation.id'] = this.clientTraceId.replace(/-/g, '').toUpperCase();
            });

            this.appInsights.loadAppInsights();
        }
    }

    private redactSensitiveInformation(properties?: Record<string, unknown>): Record<string, unknown> | undefined {
        if (!properties || properties.length === 0) {
            return properties;
        }
        for (const key of Object.keys(properties || {})) {
            if (typeof properties[key] === 'string') {
                properties[key] = redactString(properties[key]);
            }
        }
        return properties;
    }

    public isInsightsEnabled(): boolean {
        return this.insightsEnabled;
    }

    public getClientSession(): string {
        const session = this.appInsights?.context.getSessionId() ?? 'unknown';
        return `Session: ${session} :: ClientTraceId: ${this.clientTraceId}`;
    }

    public getTraceId(): string {
        const traceId = this.appInsights?.context?.telemetryTrace?.traceID || this.clientTraceId;
        return traceId.toUpperCase();
    }

    public setAppVersion(appVersion: string): void {
        this.appVersion = appVersion;
    }

    public async setUser(gid?: string, country?: string, salesOrg?: string, plant?: string): Promise<void> {
        gid = gid || 'unknown';
        this.appInsights?.setAuthenticatedUserContext(gid, `${country}::${salesOrg}::${plant}`, true);
        this.trackEvent('user logged in', { gid, country, salesOrg, plant });
    }

    public trackEvent(name: string, properties?: ICustomProperties): void {
        this.appInsights?.trackEvent({ name }, truncateAppInsightsProperties(properties));
    }

    public trackTrace(trace: ITraceTelemetry, customProperties?: ICustomProperties): void {
        this.appInsights?.trackTrace(trace, truncateAppInsightsProperties(customProperties));
    }

    public trackException(error: IExceptionTelemetry, props?: ICustomProperties): void {
        this.appInsights?.trackException(error, truncateAppInsightsProperties(props));
    }

    public trackDependency(dependency: {
        id: string;
        target?: string;
        name?: string;
        data?: string;
        duration?: number;
        responseCode: number;
        success?: boolean;
        type?: string;
    }): void {
        this.appInsights?.trackDependencyData(dependency);
    }

    public trackPage(name?: string, uri?: string): void {
        this.appInsights?.trackPageView({ name, uri });
    }
}
